import Chart from 'chart.js/auto';
import { pageQuery }            from '../lib/egress.js';
import { COLORS, CHART_DEFAULTS, rateColor } from '../lib/theme.js';
import { kpiCard, icons }       from '../components/kpi-card.js';
import { openMuniDrill, openProgDrill } from './drills.js';

const charts = {};
function destroyChart(id) {
  if (charts[id]) { charts[id].destroy(); delete charts[id]; }
}

export async function renderOverview() {
  const el = document.getElementById('view-overview');
  const todayIso = isoLocalDate(new Date());

  el.innerHTML = `
    <div class="page-header">
      <div class="page-header-row">
        <div>
          <h1>Dashboard Overview</h1>
          <p>Real-time metrics across all municipal learnership programmes</p>
        </div>
      </div>
    </div>
    <div id="kpi-grid" class="kpi-grid"></div>

    <div class="chart-card" id="phase-card" style="margin-bottom:24px">
      <div class="chart-card-header">
        <div>
          <div class="chart-card-title">Training Phase by Municipality</div>
          <div class="chart-card-sub">In Class (Elundini &amp; Matatiele) vs Practical Experience</div>
        </div>
        <div id="phase-totals" style="font-family:'Syne',sans-serif;font-size:1.1rem;font-weight:800;display:flex;gap:20px"></div>
      </div>
      <div id="phase-body" style="padding:0 20px 16px"></div>
    </div>

    <div class="chart-card" id="today-att-card" style="margin-bottom:24px">
      <div class="chart-card-header">
        <div>
          <div class="chart-card-title">Attendance</div>
          <div class="chart-card-sub" id="today-att-date">Loading…</div>
        </div>
        <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
          <div style="display:flex;align-items:center;gap:3px">
            <button id="att-prev-btn" title="Previous day"
              style="background:none;border:1px solid var(--border);border-radius:6px;cursor:pointer;font-size:1.2rem;line-height:1;padding:2px 9px;color:var(--text-muted)">&#8249;</button>
            <input type="date" id="att-date-input" value="${todayIso}" max="${todayIso}"
              style="border:1px solid var(--border);border-radius:6px;padding:3px 8px;font-family:inherit;font-size:.82rem;background:var(--bg-card,#fff);color:var(--text-primary)">
            <button id="att-next-btn" title="Next day" disabled
              style="background:none;border:1px solid var(--border);border-radius:6px;cursor:pointer;font-size:1.2rem;line-height:1;padding:2px 9px;color:var(--text-muted);opacity:.35">&#8250;</button>
          </div>
          <div id="today-att-overall" style="font-family:'Syne',sans-serif;font-size:2rem;font-weight:800"></div>
        </div>
      </div>
      <div id="today-att-body" style="padding:0 20px 16px"></div>
    </div>

    <div class="chart-stack">
      <div class="chart-card">
        <div class="chart-card-header">
          <div>
            <div class="chart-card-title">Learners by Municipality</div>
            <div class="chart-card-sub">Enrollment across regions</div>
          </div>
        </div>
        <div class="chart-wrap"><canvas id="ch-muni"></canvas></div>
        <div class="click-hint">↳ Click any bar to drill down</div>
      </div>

      <div class="chart-card">
        <div class="chart-card-header">
          <div>
            <div class="chart-card-title">Learners by Programme</div>
            <div class="chart-card-sub">Enrollment across programme types</div>
          </div>
        </div>
        <div class="chart-wrap"><canvas id="ch-prog"></canvas></div>
        <div class="click-hint">↳ Click any bar to drill down</div>
      </div>
    </div>
  `;

  const [kpis, byMuni, byProg, todayVenues, trainingPhase] = await Promise.all([
    pageQuery('dashboard_kpis_view'),
    pageQuery('learners_by_municipality',  { order: 'learner_count', ascending: false }),
    pageQuery('learners_by_program_view',  { order: 'learner_count', ascending: false }),
    pageQuery('daily_attendance_by_venue', { eq: { session_date: todayIso } }),
    pageQuery('training_phase_by_municipality'),
  ]);

  const k = kpis.data[0] || {};

  const phaseRows         = trainingPhase.data || [];
  const inClassLearners   = phaseRows.filter(r => r.training_phase === 'In Class').reduce((s, r) => s + (+r.active_learners || 0), 0);
  const practicalLearners = phaseRows.filter(r => r.training_phase === 'Practical Exp').reduce((s, r) => s + (+r.active_learners || 0), 0);

  document.getElementById('kpi-grid').innerHTML = [
    kpiCard({ label: 'Total Learners',     value: (k.total_learners ?? 0).toLocaleString(),  sub: 'All registered',              color: COLORS.orange, icon: icons.users }),
    kpiCard({ label: 'Active Learners',    value: (k.active_learners ?? 0).toLocaleString(), sub: 'Currently enrolled',          color: COLORS.blue,   icon: icons.user }),
    kpiCard({ label: 'Programmes',         value: k.active_programs ?? 0,                    sub: 'Active programmes',           color: COLORS.green,  icon: icons.book }),
    kpiCard({ label: 'Facilitators',       value: k.facilitators ?? 0,                       sub: 'Distinct people',             color: COLORS.purple, icon: icons.pin }),
    kpiCard({ label: 'Avg Attendance',     value: (k.avg_attendance_marked ?? 0) + '%',      sub: 'In-class only',               color: rateColor(+k.avg_attendance_marked || 0), icon: icons.chart }),
    kpiCard({ label: 'Marking Compliance', value: (k.marking_compliance_pct ?? 0) + '%',     sub: 'Rows actually marked',        color: rateColor(+k.marking_compliance_pct || 0), icon: icons.check }),
  ].join('');

  // Training Phase breakdown
  (() => {
    const inClassRows   = phaseRows.filter(r => r.training_phase === 'In Class');
    const practicalRows = phaseRows.filter(r => r.training_phase === 'Practical Exp');

    document.getElementById('phase-totals').innerHTML = `
      <span style="color:${COLORS.teal}">${inClassLearners.toLocaleString()} <span style="font-size:.7rem;font-weight:400;color:var(--text-muted)">in class</span></span>
      <span style="color:${COLORS.amber}">${practicalLearners.toLocaleString()} <span style="font-size:.7rem;font-weight:400;color:var(--text-muted)">practical</span></span>
    `;

    document.getElementById('phase-body').innerHTML = `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:4px">
        <div>
          <div style="font-weight:700;font-size:.75rem;color:${COLORS.teal};text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;padding-bottom:6px;border-bottom:2px solid ${COLORS.teal}">
            In Class &mdash; ${inClassLearners.toLocaleString()} learners
          </div>
          ${inClassRows.map(r => phaseRow(r, COLORS.teal)).join('')}
        </div>
        <div>
          <div style="font-weight:700;font-size:.75rem;color:${COLORS.amber};text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;padding-bottom:6px;border-bottom:2px solid ${COLORS.amber}">
            Practical Exp &mdash; ${practicalLearners.toLocaleString()} learners
          </div>
          ${practicalRows.map(r => phaseRow(r, COLORS.amber)).join('')}
        </div>
      </div>
    `;
  })();

  // Attendance section — render with pre-fetched data, then wire up date navigation
  renderAttendanceBody(todayIso, todayVenues.data || []);

  async function loadAttendanceSection(dateIso) {
    document.getElementById('today-att-body').innerHTML =
      `<p style="font-size:.82rem;color:var(--text-muted)">Loading…</p>`;
    document.getElementById('att-date-input').value = dateIso;
    const nextBtn = document.getElementById('att-next-btn');
    nextBtn.disabled = dateIso >= todayIso;
    nextBtn.style.opacity = dateIso >= todayIso ? '.35' : '1';

    const { data: rows } = await pageQuery('daily_attendance_by_venue', { eq: { session_date: dateIso } });
    renderAttendanceBody(dateIso, rows || []);
  }

  document.getElementById('att-date-input').addEventListener('change', e => {
    if (e.target.value) loadAttendanceSection(e.target.value);
  });

  document.getElementById('att-prev-btn').addEventListener('click', () => {
    const curr = document.getElementById('att-date-input').value || todayIso;
    const d = new Date(curr + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    loadAttendanceSection(isoLocalDate(d));
  });

  document.getElementById('att-next-btn').addEventListener('click', () => {
    const curr = document.getElementById('att-date-input').value || todayIso;
    const d = new Date(curr + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    const next = isoLocalDate(d);
    if (next <= todayIso) loadAttendanceSection(next);
  });

  // Learners by Municipality
  destroyChart('ch-muni');
  const muniRows = byMuni.data;
  charts['ch-muni'] = new Chart(document.getElementById('ch-muni'), {
    type: 'bar',
    data: {
      labels: muniRows.map(r => r.municipality_name),
      datasets: [{
        data: muniRows.map(r => r.learner_count),
        backgroundColor: COLORS.blue,
        hoverBackgroundColor: COLORS.orange,
        borderRadius: 6, borderSkipped: false
      }]
    },
    options: { ...CHART_DEFAULTS }
  });
  document.getElementById('ch-muni').addEventListener('click', (evt) => {
    const pts = charts['ch-muni'].getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);
    if (!pts.length) return;
    const r = muniRows[pts[0].index];
    openMuniDrill({ muniId: r.municipality_id, muniName: r.municipality_name });
  });

  // Learners by Programme
  destroyChart('ch-prog');
  const progRows = byProg.data;
  charts['ch-prog'] = new Chart(document.getElementById('ch-prog'), {
    type: 'bar',
    data: {
      labels: progRows.map(r => shortName(r.program_name || 'Unassigned')),
      datasets: [{
        data: progRows.map(r => r.learner_count),
        backgroundColor: COLORS.teal,
        hoverBackgroundColor: COLORS.orange,
        borderRadius: 6, borderSkipped: false
      }]
    },
    options: { ...CHART_DEFAULTS }
  });
  document.getElementById('ch-prog').addEventListener('click', (evt) => {
    const pts = charts['ch-prog'].getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);
    if (!pts.length) return;
    const r = progRows[pts[0].index];
    if (!r.programme_id) return;
    openProgDrill({ programmeId: r.programme_id, programmeName: r.program_name });
  });
}

function renderAttendanceBody(dateIso, rows) {
  const muniMap = {};
  for (const r of rows) {
    if (!muniMap[r.municipality_id]) {
      muniMap[r.municipality_id] = { id: r.municipality_id, name: r.municipality_name, present: 0, total: 0 };
    }
    muniMap[r.municipality_id].present += r.present_count;
    muniMap[r.municipality_id].total   += r.total_marked;
  }
  const muniList     = Object.values(muniMap).sort((a, b) => b.present - a.present);
  const totalPresent = muniList.reduce((s, m) => s + m.present, 0);
  const totalMarked  = muniList.reduce((s, m) => s + m.total,   0);
  const overallPct   = totalMarked > 0 ? Math.round(totalPresent / totalMarked * 100) : 0;
  const pctColor     = rateColor(overallPct);

  const d = new Date(dateIso + 'T00:00:00');
  document.getElementById('today-att-date').textContent =
    d.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  document.getElementById('today-att-overall').innerHTML =
    `<span style="color:${pctColor}">${overallPct}%</span>
     <span style="font-size:.9rem;font-weight:400;color:var(--text-muted);margin-left:6px">${totalPresent} / ${totalMarked}</span>`;

  const body = document.getElementById('today-att-body');
  if (!muniList.length) {
    body.innerHTML = `<p style="font-size:.82rem;color:var(--text-muted)">No attendance marked for this date.</p>`;
    return;
  }
  body.innerHTML = muniList.map(m => {
    const pct = m.total > 0 ? Math.round(m.present / m.total * 100) : 0;
    const col = rateColor(pct);
    return `
      <div class="drill-venue-row clickable-row" data-muni-id="${esc(m.id)}" data-muni-name="${esc(m.name)}"
           style="cursor:pointer;padding:6px 0">
        <div class="drill-venue-left">
          <span class="drill-venue-name">${esc(m.name)}</span>
          <span class="drill-venue-sub">${m.present} present · ${m.total} marked</span>
        </div>
        <div class="drill-venue-right">
          <span style="font-weight:700;color:${col}">${pct}%</span>
          <div class="progress-bar" style="width:80px"><div class="progress-fill" style="width:${pct}%;background:${col}"></div></div>
        </div>
      </div>`;
  }).join('');

  body.querySelectorAll('.clickable-row').forEach(row => {
    row.addEventListener('click', () => {
      openMuniDrill({ muniId: row.dataset.muniId, muniName: row.dataset.muniName, sessionDate: dateIso });
    });
  });
}

function phaseRow(r, color) {
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:7px 0;border-bottom:1px solid rgba(0,0,0,0.06)">
      <span style="font-size:.84rem">${esc(r.municipality_name)}</span>
      <span style="font-weight:700;color:${color};font-size:.84rem">${(+r.active_learners || 0).toLocaleString()}</span>
    </div>`;
}

function shortName(name) {
  return (name || '').length > 24 ? name.slice(0, 22) + '…' : name;
}

function isoLocalDate(d) {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}
